import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * InfoRow — the one read-only "label: value" line.
 *
 * Every detail screen needs this, and before there was a shared one roughly
 * thirty modules had each grown a private copy named InfoRow, Field, Line or
 * Row. They drifted, and most of them drifted the same way: a label column
 * pinned at a fixed 8–11rem. That is fine at desk width and unusable on a
 * phone, where it left about 60px for the value and broke dates and names one
 * word per line.
 *
 * So the gutter here is conditional by construction. Below `sm` the label sits
 * above its value and both get the full width; from `sm` up the two-column
 * form returns. A caller cannot reintroduce the bug by passing a width,
 * because `labelWidth` only names a size — the breakpoint is not negotiable.
 *
 *   <InfoRow label="Trip name" value={booking.name} />
 *   <InfoRow label="Status" labelWidth="sm" icon={MdFlag}>
 *     <Badge>{booking.status}</Badge>
 *   </InfoRow>
 *
 * `value` and `children` are interchangeable; `children` wins when both are
 * given, so a simple string stays a prop and rich markup stays a child.
 * A null/undefined value prints a muted em dash: a figure that was never
 * recorded must not read as an empty string someone typed.
 */
const LABEL_WIDTHS = {
  sm: 'sm:tw-w-32',
  md: 'sm:tw-w-40',
  lg: 'sm:tw-w-44',
};

const InfoRow = ({
  label,
  value,
  children,
  icon: Icon,
  labelWidth = 'md',
  divider = false,
  className = '',
  labelClassName = '',
  valueClassName = '',
}) => {
  const content = children ?? value;
  const isEmpty = content === null || content === undefined || content === '';

  return (
    <div
      className={classNames(
        'tw-flex tw-flex-col tw-gap-0.5 tw-py-2',
        'sm:tw-flex-row sm:tw-items-start sm:tw-gap-3',
        divider && 'tw-border-0 tw-border-b tw-border-solid tw-border-slate-100 last:tw-border-0',
        className
      )}
    >
      <div
        className={classNames(
          'tw-flex tw-items-center tw-gap-1.5 sm:tw-flex-shrink-0',
          LABEL_WIDTHS[labelWidth] || LABEL_WIDTHS.md,
          labelClassName
        )}
      >
        {Icon && <Icon size={12} className="tw-text-slate-400 tw-flex-shrink-0" aria-hidden />}
        <span className="tw-text-xs tw-font-medium tw-text-slate-500">{label}</span>
      </div>

      <div
        className={classNames(
          'tw-min-w-0 tw-break-words tw-text-sm tw-text-slate-700 sm:tw-flex-1',
          valueClassName
        )}
      >
        {isEmpty ? <span className="tw-text-slate-300">—</span> : content}
      </div>
    </div>
  );
};

InfoRow.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node,
  children: PropTypes.node,
  icon: PropTypes.elementType,
  labelWidth: PropTypes.oneOf(['sm', 'md', 'lg']),
  divider: PropTypes.bool,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  valueClassName: PropTypes.string,
};

export default InfoRow;
